/**
 * OpenID Connect sign-in against the realm, through oidc-client-ts.
 *
 * One `UserManager` for the app: authorization code with PKCE, and the
 * user kept in localStorage so every tab shares one sign-in. Per-tab
 * storage let a step-up in one tab break the others: their next renewal
 * came back with the new auth_time and oidc-client-ts refused it.
 * Renewal through the refresh token is `session.ts`'s, not the library's
 * timer, so a tab never runs two at once. The helpers below are the only
 * surface the rest of the app uses, so a test can swap the manager.
 */
import { UserManager, WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts';

const DEFAULT_SCOPE = 'openid profile email';
const STORE_PREFIX = 'oidc.';
/** Where the realm user lives in localStorage; another tab removing it means signed out. */
export const USER_KEY_PREFIX = `${STORE_PREFIX}user:`;
const CALLBACK_PATH = '/auth/callback';
const LOGIN_PATH = '/login';
/** Where a sign-in lands when nothing asked for a specific page. */
export const HOME_PATH = '/overview';

/** What rides in the OIDC `state` across the redirect. */
interface SignInState {
  returnTo?: string | undefined;
}

export interface OidcEnv {
  authority: string;
  clientId: string;
  scope?: string | undefined;
}

/** True when the build carries a realm to sign in against. */
export function isOidcConfigured(): boolean {
  return Boolean(import.meta.env.VITE_OIDC_AUTHORITY && import.meta.env.VITE_OIDC_CLIENT_ID);
}

function readEnv(): OidcEnv {
  return {
    authority: String(import.meta.env.VITE_OIDC_AUTHORITY ?? ''),
    clientId: String(import.meta.env.VITE_OIDC_CLIENT_ID ?? ''),
    scope: import.meta.env.VITE_OIDC_SCOPE ? String(import.meta.env.VITE_OIDC_SCOPE) : undefined,
  };
}

/** The manager settings for a realm and the page origin. */
export function buildSettings(env: OidcEnv, origin: string): UserManagerSettings {
  return {
    authority: env.authority,
    client_id: env.clientId,
    redirect_uri: `${origin}${CALLBACK_PATH}`,
    post_logout_redirect_uri: `${origin}${LOGIN_PATH}`,
    response_type: 'code',
    scope: env.scope ?? DEFAULT_SCOPE,
    // session.ts renews on the expiring event and on demand, one at a time.
    automaticSilentRenew: false,
    // No check-session iframe: the realm has no session-management endpoint
    // configured for this client, and renewal already covers expiry.
    monitorSession: false,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({ store: window.localStorage, prefix: STORE_PREFIX }),
  };
}

let manager: UserManager | null = null;

/** The app's manager, built on first use; null when the build is unconfigured. */
export function getUserManager(): UserManager | null {
  if (!isOidcConfigured()) return manager;
  if (!manager) manager = new UserManager(buildSettings(readEnv(), window.location.origin));
  return manager;
}

/** Tests inject a stand-in; `null` restores the lazy build. */
export function setUserManager(next: UserManager | null): void {
  manager = next;
}

function requireManager(): UserManager {
  const current = getUserManager();
  if (!current) throw new Error('OIDC is not configured: set VITE_OIDC_AUTHORITY and VITE_OIDC_CLIENT_ID.');
  return current;
}

/** Send the browser to the realm; `returnTo` comes back in the callback. */
export async function signIn(returnTo?: string): Promise<void> {
  const state: SignInState = { returnTo };
  await requireManager().signinRedirect({ state });
}

/**
 * Re-authenticate for a step-up: the realm asks for credentials again even
 * with a live session, so `auth_time` moves to now.
 */
export async function stepUp(returnTo: string): Promise<void> {
  const state: SignInState = { returnTo };
  await requireManager().signinRedirect({ prompt: 'login', max_age: 0, state });
}

/** End the realm session; the realm sends the browser back to /login. */
export async function signOut(): Promise<void> {
  await requireManager().signoutRedirect();
}

/** Finish the code exchange on the callback page; returns where to go next. */
export async function completeSignIn(): Promise<string> {
  const user = await requireManager().signinRedirectCallback();
  const state = user.state as SignInState | undefined;
  return state?.returnTo ?? HOME_PATH;
}
